import { QueryClient, queryOptions } from "@tanstack/react-query";

import { getOnboardingModuleVersion } from "@/components/onboarding/configs/moduleVersions";
import type {
  GetOnboardingModulesResponse,
  GetOnboardingProgressResponse,
  OnboardingProgress
} from "@/types/onboarding";
import { CustomError } from "@/utils/errors";

import {
  getOnboardingModules,
  getOnboardingProgress,
  putOnboardingProgress
} from "./onboardingApi";

const ONBOARDING_OVERVIEW_STALE_TIME = 24 * 60 * 60 * 1000;

export type OnboardingOverviewResponse = readonly [
  GetOnboardingModulesResponse,
  GetOnboardingProgressResponse
];

/**
 * Canonical query-key registry for the onboarding domain. Both the declarative
 * hooks (useGetOnboardingModulesAPI, ...) and the imperative onboarding fetches
 * (useEnterOnboardingMode) read/write the same cache through these keys.
 */
export const ONBOARDING_QUERY_KEYS = {
  overview: ["getOnboardingOverview"] as const,
  modules: ["getOnboardingModules"] as const,
  progress: (moduleId?: string) =>
    ["getOnboardingProgress", moduleId ?? "all"] as const
};

/**
 * Shared query definition for the onboarding module list. Used both with
 * useQuery (declarative) and queryClient.ensureQueryData (imperative, inside
 * event handlers) so staleTime/queryFn never drift between the two call sites.
 */
export const onboardingModulesQuery = () =>
  queryOptions<GetOnboardingModulesResponse, CustomError>({
    queryKey: ONBOARDING_QUERY_KEYS.modules,
    queryFn: getOnboardingModules,
    staleTime: ONBOARDING_OVERVIEW_STALE_TIME
  });

/**
 * Shared query definition for onboarding progress. The queryFn is a pure fetch:
 * version-normalization and cross-cache writes are owned by the imperative
 * consumer (ensureOnboardingOverviewData), never by the queryFn — a queryFn can
 * re-run on background refetch, reconnect or retry, and must not fire the
 * version-reset PUT or overwrite sibling caches as a side effect.
 * See onboardingModulesQuery.
 */
export const onboardingProgressQuery = (moduleId?: string) =>
  queryOptions<GetOnboardingProgressResponse, CustomError>({
    queryKey: ONBOARDING_QUERY_KEYS.progress(moduleId),
    queryFn: () => getOnboardingProgress(moduleId),
    staleTime: ONBOARDING_OVERVIEW_STALE_TIME
  });

export const onboardingOverviewQuery = (queryClient: QueryClient) =>
  queryOptions<OnboardingOverviewResponse, CustomError>({
    queryKey: ONBOARDING_QUERY_KEYS.overview,
    queryFn: () => ensureOnboardingOverviewData(queryClient),
    staleTime: ONBOARDING_OVERVIEW_STALE_TIME
  });

const upsertProgress = (
  progress: OnboardingProgress[],
  updatedProgress: OnboardingProgress
) => {
  const idx = progress.findIndex(
    (item) => item.module_id === updatedProgress.module_id
  );
  const next = [...progress];
  if (idx >= 0) next[idx] = updatedProgress;
  else next.push(updatedProgress);
  return next;
};

export const upsertOnboardingProgressCache = (
  queryClient: QueryClient,
  updatedProgress: OnboardingProgress
) => {
  queryClient.setQueryData<GetOnboardingProgressResponse>(
    ONBOARDING_QUERY_KEYS.progress(),
    (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        progress: upsertProgress(prev.progress, updatedProgress)
      };
    }
  );

  queryClient.setQueryData<GetOnboardingProgressResponse>(
    ONBOARDING_QUERY_KEYS.progress(updatedProgress.module_id),
    (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        progress: upsertProgress(prev.progress, updatedProgress)
      };
    }
  );

  queryClient.setQueryData<OnboardingOverviewResponse>(
    ONBOARDING_QUERY_KEYS.overview,
    (prev) => {
      if (!prev) return prev;
      return [
        prev[0],
        {
          ...prev[1],
          progress: upsertProgress(prev[1].progress, updatedProgress)
        }
      ] as const;
    }
  );
};

/**
 * Normalizes saved onboarding progress against the current frontend walkthrough
 * versions. The BE progress payload stores only `module_id`, so this receives
 * the modules response as context to resolve `module_id -> module.code -> local
 * version`.
 *
 * When a saved progress row has an older non-null `walkthrough_version`, reset
 * that module to a fresh `not_started` record before consumers render or enter
 * onboarding mode. Null versions are treated as untouched first-run progress and are
 * left as-is.
 *
 * This runs only from the imperative overview path (ensureOnboardingOverviewData),
 * which always normalizes the full all-modules payload, so the normalized result
 * is written back to the all-progress cache key.
 */
export const ensureOnboardingProgressVersions = async (
  queryClient: QueryClient,
  modulesResp: GetOnboardingModulesResponse,
  progressResp: GetOnboardingProgressResponse
): Promise<GetOnboardingProgressResponse> => {
  const modulesById = new Map(
    modulesResp.modules.map((module) => [module.id, module])
  );

  const resetProgressRows = progressResp.progress.map(async (progress) => {
    const moduleCode = modulesById.get(progress.module_id)?.code;
    const currentVersion = getOnboardingModuleVersion(moduleCode);
    const savedVersion = progress.walkthrough_version;

    if (
      !currentVersion ||
      savedVersion === null ||
      savedVersion === currentVersion
    ) {
      return progress;
    }

    const resetProgress = await putOnboardingProgress(progress.module_id, {
      current_step: 0,
      status: "not_started",
      walkthrough_version: currentVersion
    });
    upsertOnboardingProgressCache(queryClient, resetProgress);
    return resetProgress;
  });

  const normalizedProgressRows = await Promise.all(resetProgressRows);
  const normalizedProgressResp = {
    ...progressResp,
    progress: normalizedProgressRows
  };

  queryClient.setQueryData(
    ONBOARDING_QUERY_KEYS.progress(),
    normalizedProgressResp
  );

  return normalizedProgressResp;
};

/**
 * Ensures the complete onboarding overview payload is ready for consumers that
 * need modules and progress together: login warm-cache, Product Overview, and
 * onboarding entry. This is also the canonical version-normalization boundary. Once
 * both responses are available, stale saved progress is reset to the current
 * local walkthrough version before the data is written back to React Query.
 *
 * Goal: every onboarding surface reads the same normalized cache, so the
 * checklist never shows obsolete progress and entering onboarding mode does not need
 * its own version-reset branch.
 */
export const ensureOnboardingOverviewData = async (
  queryClient: QueryClient
): Promise<OnboardingOverviewResponse> => {
  const [modulesResp, progressResp] = await Promise.all([
    queryClient.ensureQueryData(onboardingModulesQuery()),
    queryClient.ensureQueryData(onboardingProgressQuery())
  ]);
  const normalizedProgressResp = await ensureOnboardingProgressVersions(
    queryClient,
    modulesResp,
    progressResp
  );
  const overviewData = [modulesResp, normalizedProgressResp] as const;

  queryClient.setQueryData(ONBOARDING_QUERY_KEYS.overview, overviewData);

  return overviewData;
};
